import React, { ReactNode, useCallback, useState } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import clsx from 'clsx';
import QuestionMark from '../../ui/icons/QuestionMark/QuestionMark';
import { useSettings } from '../../../hooks/useSettings';
import { useTranslations } from '../../../hooks/useTranslations';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import { useChatUIState } from '../ChatAgent/KitChat';
import TextLink, { TextLinkTargetType } from '../../ui/TextLink/TextLink';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import themeSettingsQuery from '../../../../../graphql/themeSettingsQuery';
import {
  GetRetailerThemeQuery,
  GetRetailerThemeQueryVariables,
} from '../../../../../__generated__/loma';
import CpCustomComponent from '../CpCustomComponent/CpCustomComponent';
import styles from './HelpPill.module.css';

export interface HelpPillProps {
  featureName?: string;
}

const HelpPill = ({ featureName }: HelpPillProps) => {
  const { helpPill } = useSettings();

  // FIXME: temp hacky solution to allow both chat and pill on page
  const {
    state: { chatExists },
  } = useChatUIState();

  const {
    helpPageHref,
    showIcon: showIconIn,
    showText: showTextIn,
    displayIconLast,
    customComponentSettingsId,
  } = helpPill || {};
  const showIcon =
    showIconIn === null || showIconIn === undefined || showIconIn;
  const showText =
    showTextIn === null || showTextIn === undefined || showTextIn;

  const { helpPillText = '' } = useTranslations();

  const { state } = usePageContext();
  const { retailerMoniker, versionName, localeLanguage, localeCountry, cache } =
    state;

  const { data } = useSuspenseQuery<
    GetRetailerThemeQuery,
    GetRetailerThemeQueryVariables
  >(themeSettingsQuery, {
    variables: {
      retailerMoniker,
      versionName,
      localeLanguage,
      localeCountry,
      featureName,
      cache,
    },
  });

  const { floatingpillNoneNone: pillStyle, iconsPrimaryNone } =
    data.retailerTheme;

  const logEvent = useAnalyticsPost();

  const [postMessage, setPostMessageImpl] = useState<
    (message: unknown) => void
  >(() => (): void => {});

  const setPostMessage = useCallback(
    (value: typeof postMessage) => {
      setPostMessageImpl(() => value);
    },
    [setPostMessageImpl],
  );

  const onClick = useCallback(() => {
    logEvent({
      clickCategory: NarvarClickSubTypes.EXTERNAL_HELP_LINK,
      clickLinkDestinationUrl: helpPageHref,
      clickLinkLabel: helpPillText,
      clickSubType: NarvarClickSubTypes.EXTERNAL_HELP_LINK,
      clickType: NarvarClickTypes.OUTGOING,
      eventComponent: NarvarEventComponents.NTH_BLOCK_HELP_PILL,
      eventFeature: NarvarEventComponents.NTH_BLOCK_HELP_PILL,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_BLOCK_HELP_PILL,
    });
    if (customComponentSettingsId) {
      postMessage({ type: 'onHelpPillClick' });
    }
  }, [
    logEvent,
    postMessage,
    customComponentSettingsId,
    helpPageHref,
    helpPillText,
    featureName,
  ]);

  const [isOpen, setIsOpen] = useState(false);

  if (!helpPageHref && !customComponentSettingsId) return null;

  const contents: (ReactNode | false)[] = [
    showIcon && (
      <div
        key="helpPillIcon"
        style={{
          padding: 0,
        }}
      >
        <QuestionMark
          containerCustomStyle={{ width: '2rem' }}
          styleIconOverrides={iconsPrimaryNone}
        />
      </div>
    ),
    showText && (
      <div
        key="helpPillText"
        style={{
          display: showIcon ? 'inherit' : 'none',
          padding: '0 0.5rem',
        }}
      >
        {helpPillText}
      </div>
    ),
  ];

  if (displayIconLast) contents.reverse();

  const containerClasses = clsx({
    [styles.container]: true,
    [styles.containerOpen]: isOpen,
  });

  return (
    <>
      <button
        className={styles.button}
        type="button"
        style={{
          // FIXME: temp hacky solution to allow both chat and pill
          bottom: chatExists ? '6rem' : '1rem',
        }}
        data-test="helpPillFeature"
        aria-label={
          !customComponentSettingsId && helpPageHref
            ? `External link to ${helpPageHref}`
            : undefined
        }
      >
        <TextLink
          href={!customComponentSettingsId && helpPageHref ? helpPageHref : ''}
          target={
            !customComponentSettingsId && helpPageHref
              ? TextLinkTargetType.BLANK
              : undefined
          }
          rel="noreferrer noopener"
          linkStyle={{
            sDefault: {
              color: '#fff',
              backgroundColor: '#000000',
              borderRadius: '5rem',
              padding: '0.375rem',
              display: 'block',
              ...pillStyle,
            },
            sHover: {
              backgroundColor: '#000000',
              textDecorationLine: 'none',
            },
          }}
          onClick={onClick}
        >
          <div className={styles.linkContents} data-testid="help-pill-grid-row">
            {contents.map((content) => content)}
          </div>
        </TextLink>
      </button>
      {customComponentSettingsId && (
        <div role="group" className={containerClasses}>
          <CpCustomComponent
            key="helpPillCustomComponent"
            featureName="customComponent"
            componentId={customComponentSettingsId}
            postMessageRef={setPostMessage}
            onIsHelpWidgetOpenChange={setIsOpen}
          />
        </div>
      )}
    </>
  );
};

export default HelpPill;
